/**
 * AcceptInvitePage — landed on by clicking the magic link in the
 * invite email (`/accept-invite?token=...`).
 *
 * Flow (deliberately tiny — most of the heavy lifting reuses the
 * existing /api/auth/send-otp + /api/auth/verify-otp endpoints):
 *
 *   1. Read `token` from the URL query string.
 *   2. POST /api/identity-invites/accept { token } — server validates
 *      the magic link is fresh and unused, returns the recipient's
 *      email + workspace_id.
 *   3. The OTP code is ALREADY in the user's inbox (sent at approval
 *      time). Ask them to type it. On submit, POST it to the standard
 *      /api/auth/verify-otp — the JWT comes back, identical to a
 *      regular passwordless login.
 *   4. Persist the token + user info via the AuthContext and route to
 *      the dashboard.
 *
 * "Resend OTP" simply calls /api/auth/send-otp which generates a fresh
 * 6-digit code and emails it (existing infra).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Loader2, ShieldCheck, AlertTriangle, KeyRound } from 'lucide-react';

// Module-level cache keyed by the raw magic-link token. The server
// consumes the magic link on the first call to /api/identity-invites/accept
// (single-use security model); a second call always fails with
// "invalid or already consumed". React 18 StrictMode runs effects twice
// on mount in development, and there are also realistic production
// scenarios where the same component re-mounts quickly (back/forward,
// browser-extension preflight, etc). Without dedup, the FIRST call's
// success state gets clobbered by the SECOND call's error → the user
// sees the "Invite link not valid" error even though they just landed.
//
// We use THREE layers of dedup, in order of fastest → most robust:
//   1. A module-level Map of in-flight promises keyed by token — handles
//      the common StrictMode-double-effect case where both effect runs
//      share the same module instance.
//   2. A `sessionStorage`-cached *successful* response keyed by token —
//      survives full component remounts (browser back/forward, HMR
//      module reloads, second tab opened to the same magic link). This
//      is the layer that saves us when layer 1 is bypassed because the
//      JS module was re-evaluated between the two effect runs.
//   3. A per-component `useRef` guard so a single mounted instance never
//      fires twice even if both upper layers somehow miss.
//
// SessionStorage is OK to use here: the magic-link token is already in
// the URL the user just clicked from their email, so it's not adding
// secrecy risk. We persist ONLY the public { email, workspace_id } the
// server returned (no JWT, no OTP) so the user can finish the flow even
// after a page refresh.
const _validateTokenInflight = new Map(); // token -> Promise<server response data>

const _sessionKeyFor = (token) => `ethira:invite:accepted:${token}`;

const _readCachedAcceptance = (token) => {
  try {
    const raw = sessionStorage.getItem(_sessionKeyFor(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.email) return parsed;
  } catch { /* sessionStorage may be unavailable in private mode */ }
  return null;
};

const _writeCachedAcceptance = (token, data) => {
  try {
    sessionStorage.setItem(
      _sessionKeyFor(token),
      JSON.stringify({ email: data.email, workspace_id: data.workspace_id, invite_id: data.invite_id }),
    );
  } catch { /* no-op */ }
};

const _validateInvite = async (token) => {
  // Layer 2: cached success from a previous load of this exact token.
  const cached = _readCachedAcceptance(token);
  if (cached) return cached;

  // Layer 1: in-flight promise for this module session.
  let p = _validateTokenInflight.get(token);
  if (!p) {
    p = axios.post('/api/identity-invites/accept', { token })
      .then((r) => {
        _writeCachedAcceptance(token, r.data);
        return r.data;
      })
      .catch((err) => {
        // Drop failed promise so a manual retry (different token) isn't
        // sticky-bound to this failure. The next call will try again.
        _validateTokenInflight.delete(token);
        throw err;
      });
    _validateTokenInflight.set(token, p);
  }
  return p;
};

const _humanizeError = (detail) => {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.detail || JSON.stringify(detail);
  }
  return '';
};

const AcceptInvitePage = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  // We hook directly into the same context setters Login.js uses after
  // /auth/verify-otp — keeps the auth-bootstrap path identical to a
  // regular passwordless sign-in (no special "invited user" code path
  // anywhere downstream).
  const { setUser, setWorkspaces, setCurrentWorkspace } = useAuth();

  const token = search.get('token') || '';
  const [phase, setPhase] = useState('validating'); // validating | otp | error | success
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // Per-component dedup guard. Prevents the second StrictMode invocation
  // of the effect (which would otherwise race the first one to the
  // backend and clobber the success UI with an error).
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      if (!token) {
        setError('Missing invite token. Please use the link from your invitation email.');
        setPhase('error');
        return;
      }
      try {
        // Shared per-token Promise so StrictMode / double-mount doesn't
        // consume the single-use magic link twice (the second consume
        // would always 400 with "invalid or already consumed").
        const data = await _validateInvite(token);
        setEmail(data?.email || '');
        setPhase('otp');
      } catch (e) {
        const detail = _humanizeError(e.response?.data?.detail);
        // The server intentionally does not leak which email an
        // unknown/consumed token was for, so we just show a generic
        // recovery message — the user still has the 6-digit code in
        // their invitation email and can use it via /login.
        setError(detail || 'This invite link is invalid, expired, or has already been used.');
        setPhase('error');
      }
    })();
    // No cleanup needed — `startedRef` guarantees the effect runs once
    // per component instance, so there's nothing to cancel on unmount.
    // setState on a genuinely-unmounted component is a no-op in React 18.
  }, [token]);

  const submitOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post('/api/auth/verify-otp', { email, otp });
      const data = r.data;
      localStorage.setItem('token', data.access_token);
      if (typeof setUser === 'function') setUser(data.user);
      if (typeof setWorkspaces === 'function') setWorkspaces(data.workspaces || []);
      if (typeof setCurrentWorkspace === 'function') setCurrentWorkspace(data.current_workspace);
      if (data.current_workspace) {
        try { localStorage.setItem('current_workspace', JSON.stringify(data.current_workspace)); } catch { /* no-op */ }
      }
      try { localStorage.setItem('user', JSON.stringify(data.user)); } catch { /* no-op */ }
      setPhase('success');
      toast.success('Welcome to Nova!');
      // Route by role, matching Login.js's post-OTP behaviour so invited
      // employees land in their normal employee dashboard.
      setTimeout(() => {
        const role = data.current_workspace?.role;
        if (role === 'super_admin') navigate('/admin', { replace: true });
        else if (role === 'auditor') navigate('/auditor/dashboard', { replace: true });
        else if (role === 'employee') navigate('/employee/dashboard', { replace: true });
        else navigate('/dashboard', { replace: true });
      }, 500);
    } catch (e) {
      const msg = _humanizeError(e.response?.data?.detail) || 'Invalid or expired code';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (!email) return;
    setResending(true);
    try {
      await axios.post('/api/auth/send-otp', { email });
      toast.success('Fresh code sent — check your inbox.');
    } catch (e) {
      toast.error(_humanizeError(e.response?.data?.detail) || 'Could not resend code');
    } finally {
      setResending(false);
    }
  };

  // Recovery affordance for the error state: drop the user on the
  // standard /login passwordless screen. We don't know the email on the
  // error path (server intentionally does not leak which email an
  // unknown/consumed token was for) so we just send them to /login in
  // OTP mode and they type their email there.
  const goToLoginWithEmail = () => {
    navigate('/login?mode=otp', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardContent className="p-8">
          {phase === 'validating' && (
            <div className="flex flex-col items-center text-center py-6" data-testid="accept-invite-validating">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Accepting your invite…</h2>
              <p className="text-sm text-slate-600">Verifying the secure link from your email.</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center" data-testid="accept-invite-error">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-xl font-semibold text-slate-900 mb-1">We couldn't open this invite</h2>
              <p className="text-sm text-slate-600 mb-4" data-testid="accept-invite-error-message">{error}</p>
              <p className="text-xs text-slate-500 mb-5">
                Your one-time login code is still in the invitation email. You can
                enter it on the sign-in page below — no need to ask for a new link.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={goToLoginWithEmail}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="accept-invite-have-code"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  I already have a 6-digit code
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  data-testid="accept-invite-go-login"
                >
                  Go to sign-in page
                </Button>
              </div>
            </div>
          )}

          {phase === 'otp' && (
            <div data-testid="accept-invite-otp">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                <h2 className="text-xl font-semibold text-slate-900">Welcome to Nova</h2>
              </div>
              <p className="text-sm text-slate-600 mb-6">
                We've sent a 6-digit code to{' '}
                <span className="font-medium text-slate-900">{email}</span>.
                Enter it below to finish signing in. Passwordless only — there is no password to set.
              </p>
              <Label htmlFor="invite-otp-input" className="text-xs">Code from your email</Label>
              <div className="flex items-center gap-2 mt-1">
                <KeyRound className="w-4 h-4 text-slate-400 shrink-0" />
                <Input
                  id="invite-otp-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  className="text-center tracking-widest text-lg font-mono"
                  data-testid="invite-otp-input"
                />
              </div>
              <Button
                onClick={submitOtp}
                disabled={submitting || otp.length < 6}
                className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                data-testid="invite-otp-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Sign in
              </Button>
              <button
                type="button"
                onClick={resendOtp}
                disabled={resending}
                className="text-xs text-slate-500 hover:text-slate-700 underline mt-3 mx-auto block"
                data-testid="invite-otp-resend"
              >
                {resending ? 'Sending…' : "Didn't get the code? Resend"}
              </button>
            </div>
          )}

          {phase === 'success' && (
            <div className="text-center py-6" data-testid="accept-invite-success">
              <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <p className="text-sm text-slate-700">Signed in — taking you to your dashboard…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
